import { textEllipsis } from '@design-system/styles'
import { css } from '@emotion/react'
import { typo } from '@mathflat/common/style'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { observer } from 'mobx-react'
import { useRef } from 'react'
import type { StyleProps } from 'src/types'

import Button from '@/component/atoms/Button'
import { Loader } from '@/component/atoms/Loader'
import { Switch } from '@/component/atoms/Switch/Switch'
import ModalTemplate from '@/component/templates/ModalTemplate'
import { assignCancelWarning } from '@/constant/modal/modalContent'
import { useGroupedStudentDualList } from '@/contentComponents/DualList/hooks/useGroupedStudentDualList'
import {
  STUDENT_LIST_OPEN_LABEL,
  useStudentListOpenState,
} from '@/contentComponents/DualList/hooks/useStudentListOpenState'
import StudentGroupedDualList from '@/contentComponents/DualList/StudentGroupedDualList'
import type { Student, StudentWorkbook, StudentWorksheet } from '@/dto'
import type { CommonError } from '@/dto/common/CommonError'
import commonRepo from '@/repository/common.repo'
import globalLoaderService from '@/service/globalLoader.service'
import modalService from '@/service/modal.service'

type StudentIds = Student['id'][]

/**
 * 초기 선택된 학생 정보
 * - studentDataRelationId가 있으면 "이미 출제됨"으로 표시되고 삭제 시 경고 모달 표시
 * - studentDataRelationId가 없으면 단순 선택 상태로 취급
 *
 * TODO: 2차원 배열을 1차원 배열로 변경 예정 (현재 2차원인 이유 없음, flat()해서 사용)
 */
export type AssignedStudent = {
  studentId: Student['id']
  /** 워크시트/워크북 ID (없으면 단순 선택 상태) */
  studentDataRelationId?: StudentWorksheet['id'] | StudentWorkbook['id']
  studentName?: string
}

/** 선택된 학생 타입 (다른 파일에서 import해서 사용) */
export type RightStudent = {
  studentId: Student['id']
  studentName: Student['name']
}

type Props = {
  title: string
  titleDescription?: string
  /**
   * 초기 선택된 학생 목록
   * - studentDataRelationId가 있으면 "이미 출제됨" 표시 + 삭제 시 경고
   * - studentDataRelationId가 없으면 단순 선택 상태
   *
   * TODO: 1차원 배열로 변경 예정 (AssignedStudent[])
   */
  assignedLists?: AssignedStudent[][]
  /** 이미 출제된 학생 제거 시 경고 표시 여부 (default: true) */
  warnDeleteAssigned?: boolean
  /** 새로 추가된 학생 제출 콜백 (기존에 없던 학생만 전달) */
  onSubmit?: (
    students: {
      id: Student['id']
      name: Student['name']
    }[],
  ) => void | Promise<void | CommonError>
  /** 이미 출제된 학생 제거 콜백 */
  onUnassign?: (
    ids: NonNullable<AssignedStudent['studentDataRelationId']>[],
    studentIds: StudentIds,
  ) => void | Promise<void> | boolean | Promise<boolean>
  /** 선택 완료 콜백 (전체 선택된 학생 전달) */
  onComplete?: (
    students: {
      studentId: Student['id']
      studentName: Student['name']
    }[],
  ) => void
  /** "이미 출제됨" 텍스트 커스터마이징 */
  renderAssignedText?: (studentId: Student['id']) => string
  /** 왼쪽 아이템에 추가 정보 표시 */
  renderLeftDescriptionElement?: (student: Student) => React.ReactElement | undefined
  /** 특정 학생 비활성화 조건 */
  confirmDisabledIds?: (students: Student[]) => Student['id'][]
  /** 경고 모달 내용 커스터마이징 */
  warningModalContent?: React.ReactElement
} & StyleProps

const AssignGroupStudentModal = ({
  title,
  titleDescription,
  assignedLists,
  warnDeleteAssigned = true,
  onSubmit,
  onUnassign,
  onComplete,
  renderAssignedText,
  renderLeftDescriptionElement,
  confirmDisabledIds,
  style,
  className = '',
  warningModalContent,
}: Props) => {
  // assignedLists를 1차원으로 flat
  const mergedAssignedLists: AssignedStudent[] = assignedLists?.flat() ?? []
  // studentDataRelationId가 있는 학생 Set (이미 출제됨, unassign 시 제거)
  const assignedWithRelationSetRef = useRef(
    new Set(
      mergedAssignedLists
        .filter(({ studentDataRelationId }) => studentDataRelationId !== undefined)
        .map(({ studentId }) => studentId),
    ),
  )
  const { isDefaultOpen, setIsDefaultOpen } = useStudentListOpenState({
    fallbackValue: commonRepo.academy?.가상학생등록여부,
  })

  // useStudentDualList (학생 데이터 fetch + DualList 상태 관리)
  const { dualListBox, rightItems, isLoading, isChanged, movedToRightItems, groupedDualListProps } =
    useGroupedStudentDualList({
      initialRightItemIds: mergedAssignedLists.map(({ studentId }) => studentId),
      defaultOpen: isDefaultOpen,
    })

  // 왼쪽/오른쪽 아이템 추가 정보 렌더링
  const renderItemExtra = ({ item, side }: { item: Student; side: 'left' | 'right' }) => {
    if (side === 'left') {
      return renderLeftDescriptionElement?.(item)
    }
    // studentDataRelationId가 있는 경우에만 "이미 출제됨" 표시
    if (side === 'right' && assignedWithRelationSetRef.current.has(item.id)) {
      return (
        <span css={S.assignedLabel}>
          {renderAssignedText?.(item.id) ?? `이미 ${tJosa('출제')}됨`}
        </span>
      )
    }
    return null
  }
  // 비활성화 필터
  const disabledFilter = (student: Student) => {
    if (!confirmDisabledIds) return false
    const disabledIds = confirmDisabledIds([student])
    return disabledIds.includes(student.id)
  }
  // 왼쪽으로 이동 전 콜백 (이미 출제된 학생 경고)
  const handleBeforeMoveToLeft = async (items: Student[]): Promise<boolean> => {
    // studentDataRelationId가 있는 학생만 경고 대상
    const assignedItems = items.filter((item) => assignedWithRelationSetRef.current.has(item.id))
    // 경고 대상이 없으면 바로 이동
    if (assignedItems.length === 0) {
      return true
    }
    // 경고 비활성화된 경우
    if (!warnDeleteAssigned) {
      return true
    }
    // 경고 모달 표시
    return new Promise((resolve) => {
      modalService.openWarningModal({
        content: warningModalContent ?? assignCancelWarning.default(),
        onClick: async () => {
          try {
            // 삭제할 studentDataRelationId 수집
            const idsToUnassign: NonNullable<AssignedStudent['studentDataRelationId']>[] = []
            const studentIdsToUnassign: Student['id'][] = []
            for (const item of assignedItems) {
              const relations = mergedAssignedLists
                .filter((a) => a.studentId === item.id && a.studentDataRelationId !== undefined)
                .map((a) => a.studentDataRelationId!)
              if (relations.length > 0) {
                idsToUnassign.push(...relations)
                studentIdsToUnassign.push(item.id)
              }
            }
            const isNotCompleted = await onUnassign?.(idsToUnassign, studentIdsToUnassign)
            if (!isNotCompleted) {
              // unassign 성공: Set에서 제거 + 초기 상태에서 해당 ID 제거
              for (const id of studentIdsToUnassign) {
                assignedWithRelationSetRef.current.delete(id)
              }
              dualListBox?.removeFromInitialState(studentIdsToUnassign)
            }
            resolve(!isNotCompleted)
          } catch (error) {
            resolve(false)
          }
        },
      })
    })
  }
  // 제출 처리
  const handleSubmit = async () => {
    globalLoaderService.openLoader()
    try {
      // 전체 선택된 학생 콜백
      onComplete?.(
        rightItems.map((item) => ({
          studentId: item.id,
          studentName: item.name,
        })),
      )
      // 새로 추가된 학생 제출 (movedToRightItems = 초기 left → right로 이동한 학생)
      if (movedToRightItems.length > 0 && onSubmit) {
        await onSubmit(
          movedToRightItems.map((item) => ({
            id: item.id,
            name: item.name,
          })),
        )
      }
      modalService.closeModal()
    } finally {
      globalLoaderService.closeLoader()
    }
  }
  // 로딩 중
  if (isLoading) {
    return (
      <ModalTemplate
        css={S.modal}
        style={style}
        className={className}
        header={
          <header css={S.header}>
            <h3 className="title">{title}</h3>
            {titleDescription && <sub>{titleDescription}</sub>}
          </header>
        }
        body={
          <div css={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Loader />
          </div>
        }
        footer={<footer css={S.footer} />}
      />
    )
  }

  return (
    <ModalTemplate
      css={S.modal}
      style={style}
      className={className}
      header={
        <header css={S.header}>
          <h3 className="title">{title}</h3>
          {titleDescription && <sub>{titleDescription}</sub>}
        </header>
      }
      body={
        <div css={S.body}>
          <StudentGroupedDualList
            dualListBox={dualListBox}
            searchPlaceholder="학생 이름 검색"
            rightHeaderTitle="선택된 학생"
            renderItemExtra={renderItemExtra}
            disabledFilter={confirmDisabledIds ? disabledFilter : undefined}
            onBeforeMoveToLeft={handleBeforeMoveToLeft}
            {...groupedDualListProps}
          />
        </div>
      }
      footer={
        <footer css={S.footer}>
          <div className="toggle-area">
            <Switch
              size="s"
              checked={isDefaultOpen}
              onCheckedChange={() => setIsDefaultOpen(!isDefaultOpen)}
            />
            <span>{STUDENT_LIST_OPEN_LABEL}</span>
          </div>
          <div className="button-area">
            <Button
              width={120}
              size="l"
              variant="solid"
              color="accent"
              disabled={!isChanged}
              onClick={handleSubmit}
            >
              선택 완료
            </Button>
          </div>
        </footer>
      }
    />
  )
}

const S = {
  modal: css`
    width: 600px;
    height: 620px;
  `,
  header: css`
    display: flex;
    align-items: center;
    height: 72px;
    padding: 24px;
    border-bottom: 1px solid var(--Colors-Gray-400);
    > .title {
      flex-shrink: 0;
      font-weight: 700;
      ${typo.heading05};
    }
    > sub {
      margin: 0px 24px 0px 12px;
      color: var(--Colors-Gray-700);
      ${typo.body02};
      ${textEllipsis(2)};
    }
  `,
  body: css`
    display: flex;
    flex: 1;
    padding: 16px 24px 0 24px;
    overflow: hidden;
  `,
  footer: css`
    display: flex;
    justify-content: space-between;
    padding: 16px 24px 20px 24px;
    .toggle-area {
      display: flex;
      gap: 8px;
      margin-top: auto;
      margin-bottom: auto;
    }
  `,
  assignedLabel: css`
    display: flex;
    align-items: center;
    color: var(--Colors-Gray-700);
    font-weight: 500;
    ${typo.caption02};

    &::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: var(--Radius-Circle);
      background-color: var(--Colors-Sky-400);
      margin-right: 4px;
    }
  `,
}

export default observer(AssignGroupStudentModal)
